// client/src/components/admin/project/ProjectCompletionList.js
import React, { useState, useEffect, useCallback } from 'react';
import '../../../styles/admin/project/ProjectCompletion.css';
import ProjectDetailModal from '../../marketer/project/modals/ProjectDetailModal';
import ResultStatusModal from './ResultStatusModal';
import CPSSettlementModal from './CPSSettlementModal';
import ProjectCompletionValidationModal from './ProjectCompletionValidationModal';
import ProjectCompletionRefundModal from './ProjectCompletionRefundModal';

const API_URL = process.env.REACT_APP_API_URL;

export default function ProjectCompletionList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailData, setDetailData] = useState(null);
  
  // 모달 상태
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showCPSModal, setShowCPSModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  // 검증 및 환불 데이터
  const [validationData, setValidationData] = useState(null);
  const [refundData, setRefundData] = useState(null);

  // 페이지네이션 상태 (프론트엔드 처리)
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  // 프로젝트 선택 및 상세 조회
  const handleSelectProject = useCallback(async (project) => {
    setSelectedProject(project);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${project.id}/completion-detail`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setDetailData(data.project);
      } else {
        alert('상세 정보 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('상세 정보 조회 오류:', error);
      alert('상세 정보 조회 중 오류가 발생했습니다.');
    }
  }, []);

  // 완료 처리 대상 프로젝트 목록 조회
  const fetchCompletionProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/completion-list`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setProjects(data.projects || []);
        // 첫 번째 항목 자동 선택
        if (data.projects && data.projects.length > 0) {
          handleSelectProject(data.projects[0]);
        } else {
          setSelectedProject(null);
          setDetailData(null);
        }
      } else {
        alert('목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('완료 처리 대상 목록 조회 오류:', error);
      alert('목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [handleSelectProject]);

  useEffect(() => {
    fetchCompletionProjects();
  }, [fetchCompletionProjects]);

  // 프로젝트 상세보기
  const handleShowProjectDetail = () => {
    if (detailData?.fullProjectData) {
      setShowProjectModal(true);
    }
  };

  // 결과물 상태보기
  const handleShowResultStatus = () => {
    if (detailData?.id) {
      setShowResultModal(true);
    }
  };

  // CPS 결산 상태보기
  const handleShowCPSSettlement = () => {
    if (detailData?.id) {
      setShowCPSModal(true);
    }
  };

  // 진행 완료 처리
  const handleCompleteProject = async () => {
    if (!detailData?.id) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${detailData.id}/validate-completion`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json();

      if (!data.ok) {
        alert(data.message || '검증에 실패했습니다.');
        return;
      }

      if (!data.canComplete) {
        // 검증 실패 - 모달 표시
        setValidationData(data.validation);
        setShowValidationModal(true);
      } else {
        // 검증 성공 - 환불 정보 모달 표시
        setRefundData(data.refundCalculation);
        setShowRefundModal(true);
      }
    } catch (error) {
      console.error('완료 검증 오류:', error);
      alert('완료 검증 중 오류가 발생했습니다.');
    }
  };

  // 완료 처리 후 콜백
  const handleProjectCompleted = () => {
    // 목록 새로고침
    fetchCompletionProjects();
    // 모달 초기화
    setValidationData(null);
    setRefundData(null);
  };

  // 날짜 포맷팅
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  // 금액 포맷팅
  const formatAmount = (amount) => {
    return amount ? amount.toLocaleString('ko-KR') : '0';
  };

  // --- 페이지네이션 로직 ---
  const totalCount = projects.length;
  const totalPages = Math.ceil(totalCount / limit);
  
  // 현재 페이지 데이터 슬라이싱
  const currentProjects = projects.slice(
    (currentPage - 1) * limit,
    currentPage * limit
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  if (loading) {
    return <div className="project-completion-list__loading">로딩 중...</div>;
  }

  if (projects.length === 0) {
    return (
      <div className="project-completion-list__empty">
        완료 처리할 프로젝트가 없습니다.
      </div>
    );
  }

  return (
    <div className="project-completion-list">
      <h2 className="project-completion-list__title">
        프로젝트 완료 처리
      </h2>

      <div className="project-completion-list__content">
        {/* 왼쪽 박스: 프로젝트 목록 */}
        <div className="project-completion-list__sidebar">
          <h3 className="project-completion-list__sidebar-title">
            완료 처리 대상 프로젝트 ({totalCount}개)
          </h3>

          <div className="project-completion-list__projects">
            {currentProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleSelectProject(project)}
                className={`project-completion-list__project-item ${
                  selectedProject?.id === project.id ? 'project-completion-list__project-item--selected' : ''
                }`}
              >
                <div className="project-completion-list__project-number">
                  {project.projectNumber}
                </div>
                <div className="project-completion-list__project-marketer">
                  광고주: {project.marketerName}
                </div>
                <div className="project-completion-list__project-date">
                  정산일: {formatDate(project.completionAvailableDate)}
                </div>
              </div>
            ))}
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="project-completion-list__pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="project-completion-list__pagination-btn"
              >
                처음
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="project-completion-list__pagination-btn"
              >
                이전
              </button>
              
              {getPageNumbers().map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`project-completion-list__pagination-btn ${
                    currentPage === pageNum ? 'project-completion-list__pagination-btn--active' : ''
                  }`}
                >
                  {pageNum}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="project-completion-list__pagination-btn"
              >
                다음
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="project-completion-list__pagination-btn"
              >
                마지막
              </button>
            </div>
          )}
        </div>

        {/* 오른쪽 박스: 상세 정보 */}
        <div className="project-completion-list__detail">
          {!detailData ? (
            <div className="project-completion-list__detail-empty">
              프로젝트를 선택해주세요
            </div>
          ) : (
            <>
              {/* 상세 정보 */}
              <div className="project-completion-list__detail-section">
                <h3 className="project-completion-list__detail-title">
                  프로젝트 상세 정보
                </h3>

                <div className="project-completion-list__detail-grid">
                  {/* 기본 정보 */}
                  <div className="project-completion-list__detail-label">프로젝트 번호:</div>
                  <div className="project-completion-list__detail-value">{detailData.projectNumber}</div>

                  <div className="project-completion-list__detail-label">상품명:</div>
                  <div className="project-completion-list__detail-value">{detailData.productName || '-'}</div>

                  <div className="project-completion-list__detail-label">광고주명:</div>
                  <div className="project-completion-list__detail-value">{detailData.marketerName}</div>

                  <div className="project-completion-list__detail-label">광고주 ID:</div>
                  <div className="project-completion-list__detail-value">{detailData.marketerId}</div>

                  {/* 구분선 */}
                  <div className="project-completion-list__detail-divider" />

                  {/* 일정 정보 */}
                  <div className="project-completion-list__detail-label">프로젝트 생성일:</div>
                  <div className="project-completion-list__detail-value">{formatDate(detailData.scheduleInfo?.createdAt)}</div>

                  <div className="project-completion-list__detail-label">모집 및 선정 마감일:</div>
                  <div className="project-completion-list__detail-value">{formatDate(detailData.scheduleInfo?.recruitEnd)}</div>

                  <div className="project-completion-list__detail-label">결과물 제출 마감일:</div>
                  <div className="project-completion-list__detail-value">{formatDate(detailData.scheduleInfo?.submitEnd)}</div>

                  {detailData.hasCPS && detailData.scheduleInfo?.settlementDates?.length > 0 && (
                    <>
                      <div className="project-completion-list__detail-label">결산일들:</div>
                      <div className="project-completion-list__detail-value">
                        {detailData.scheduleInfo.settlementDates.map((date, idx) => (
                          <div key={idx} className="project-completion-list__settlement-date">
                            {idx + 1}회차: {formatDate(date)}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="project-completion-list__detail-label">완료 처리 가능일:</div>
                  <div className="project-completion-list__detail-value">{formatDate(detailData.completionAvailableDate)}</div>

                  {/* 구분선 */}
                  <div className="project-completion-list__detail-divider" />

                  {/* 결제 금액 상세 */}
                  <div className="project-completion-list__detail-label">기본 운영비:</div>
                  <div className="project-completion-list__detail-value">{formatAmount(detailData.paymentDetails?.baseOp)}원</div>

                  <div className="project-completion-list__detail-label">리워드 총액:</div>
                  <div className="project-completion-list__detail-value">{formatAmount(detailData.paymentDetails?.rewardTotal)}원</div>

                  {detailData.hasCPS && (
                    <>
                      <div className="project-completion-list__detail-label">구매당 리워드 예산:</div>
                      <div className="project-completion-list__detail-value">{formatAmount(detailData.paymentDetails?.perBudget)}원</div>
                    </>
                  )}

                  <div className="project-completion-list__detail-label project-completion-list__detail-label--total">
                    총 결제 금액:
                  </div>
                  <div className="project-completion-list__detail-value project-completion-list__detail-value--total">
                    {formatAmount(detailData.paymentDetails?.deposit)}원
                  </div>

                  {/* 구분선 */}
                  <div className="project-completion-list__detail-divider" />

                  {/* 인원 정보 */}
                  <div className="project-completion-list__detail-label">계약 인원:</div>
                  <div className="project-completion-list__detail-value">{detailData.contractedCount}명</div>

                  <div className="project-completion-list__detail-label">모집 인원:</div>
                  <div className="project-completion-list__detail-value">{detailData.recruitHeadcount}명</div>

                  <div className="project-completion-list__detail-label">구매당 리워드:</div>
                  <div className="project-completion-list__detail-value">
                    {detailData.hasCPS ? (
                      <span className="project-completion-list__cps-enabled">✓ 설정됨</span>
                    ) : (
                      <span className="project-completion-list__cps-disabled">미설정</span>
                    )}
                  </div>
                </div>
              </div>

              {/* 구분선 */}
              <div className="project-completion-list__section-divider" />

              {/* 버튼 섹션 */}
              <div className="project-completion-list__actions-section">
                <h4 className="project-completion-list__actions-title">
                  관리 기능
                </h4>

                <div className="project-completion-list__actions">
                  <button
                    onClick={handleShowProjectDetail}
                    className="project-completion-list__action-btn project-completion-list__action-btn--primary"
                  >
                    📋 프로젝트 상세보기
                  </button>

                  <button
                    onClick={handleShowResultStatus}
                    className="project-completion-list__action-btn project-completion-list__action-btn--success"
                  >
                    📊 결과물 상태보기
                  </button>

                  <button
                    onClick={handleShowCPSSettlement}
                    disabled={!detailData.hasCPS}
                    className={`project-completion-list__action-btn ${
                      detailData.hasCPS 
                        ? 'project-completion-list__action-btn--purple' 
                        : 'project-completion-list__action-btn--disabled'
                    }`}
                  >
                    💰 CPS 결산 상태 보기 {!detailData.hasCPS && '(미설정)'}
                  </button>

                  <button
                    onClick={handleCompleteProject}
                    className="project-completion-list__action-btn project-completion-list__action-btn--complete"
                  >
                    ✅ 진행 완료 처리
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 프로젝트 상세보기 모달 */}
      {showProjectModal && detailData?.fullProjectData && (
        <ProjectDetailModal
          open={showProjectModal}
          onClose={() => setShowProjectModal(false)}
          project={detailData.fullProjectData}
        />
      )}

      {/* 결과물 상태보기 모달 */}
      {showResultModal && detailData?.id && (
        <ResultStatusModal
          open={showResultModal}
          onClose={() => setShowResultModal(false)}
          projectId={detailData.id}
          projectNumber={detailData.projectNumber}
        />
      )}

      {/* CPS 결산 상태보기 모달 */}
      {showCPSModal && detailData?.id && (
        <CPSSettlementModal
          open={showCPSModal}
          onClose={() => setShowCPSModal(false)}
          projectId={detailData.id}
          projectNumber={detailData.projectNumber}
        />
      )}

      {/* 검증 실패 모달 */}
      {showValidationModal && validationData && (
        <ProjectCompletionValidationModal
          open={showValidationModal}
          onClose={() => setShowValidationModal(false)}
          validation={validationData}
        />
      )}

      {/* 환불 정보 모달 */}
      {showRefundModal && refundData && detailData && (
        <ProjectCompletionRefundModal
          open={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          projectId={detailData.id}
          projectNumber={detailData.projectNumber}
          refundCalculation={refundData}
          onComplete={handleProjectCompleted}
        />
      )}
    </div>
  );
}
